<template>
  <div
    class="d-inline-block"
    v-bind="$attrs"
  >
    <!--
      Vuetify 2 v-edit-dialog was removed in V3.
      Menu-based inline edit preserves display → edit → save for table cells.
    -->
    <v-menu
      v-model="menuOpen"
      location="bottom"
      :close-on-content-click="false"
    >
      <template #activator="{ props: activatorProps }">
        <span
          class="base-editable-field__display"
          v-bind="activatorProps"
        >
          <span v-if="!$slots.display">{{ displayValue }}</span>
          <slot name="display"></slot>
        </span>
      </template>

      <v-card
        class="pa-2"
        min-width="220"
      >
        <v-text-field
          v-if="(!type && !$slots.default) || (type === 'string')"
          :model-value="boundValue"
          density="compact"
          hide-details
          single-line
          autofocus
          @update:model-value="onInput"
          @keyup.enter="save"
        ></v-text-field>
        <v-text-field
          v-else-if="type === 'number'"
          :model-value="boundValue"
          type="number"
          density="compact"
          hide-details
          single-line
          autofocus
          @update:model-value="onInput"
          @keyup.enter="save"
        ></v-text-field>
        <base-currency-picker
          v-else-if="type === 'currency'"
          :model-value="boundValue"
          clearable
          single-line
          density="compact"
          hide-details
          @update:model-value="onInput"
          @change="onCurrencyChange"
        ></base-currency-picker>
        <div v-else>
          <slot />
        </div>

        <div
          v-if="showsSaveActions"
          class="d-flex justify-end mt-2"
        >
          <v-btn
            size="small"
            variant="text"
            @click="cancel"
          >Cancel</v-btn>
          <v-btn
            size="small"
            color="primary"
            variant="text"
            @click="save"
          >Save</v-btn>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { formatAmount } from '@/utils/formatMoney';

export default {
  name: 'BaseDataTableEditableField',

  inheritAttrs: false,

  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    type: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '',
    },
  },

  emits: ['save', 'input', 'change', 'update:modelValue'],

  data: () => ({
    menuOpen: false,
  }),

  computed: {
    boundValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    displayValue() {
      const current = this.boundValue;
      if (!(current || current === 0)) {
        return 'NULL';
      }
      if (this.type === 'number' && this.currency) {
        const formatted = formatAmount(current, this.currency);
        return formatted !== null ? formatted : 'NULL';
      }
      return current;
    },

    showsSaveActions() {
      // Custom default slot (e.g. entity pickers) persists via @change; no Save row.
      if (this.$slots.default && !this.type) return false;
      return true;
    },
  },

  methods: {
    onInput(next) {
      this.$emit('update:modelValue', next);
      this.$emit('input', next);
    },

    onCurrencyChange(next) {
      this.onInput(next);
      this.$emit('change', next);
      this.$emit('save', next);
      this.menuOpen = false;
    },

    save() {
      this.$emit('save', this.boundValue);
      this.menuOpen = false;
    },

    cancel() {
      this.menuOpen = false;
    },
  },
};
</script>

<style scoped>
.base-editable-field__display {
  cursor: pointer;
  display: inline-block;
  min-width: 1ch;
}
</style>
